import React from 'react';
import { useLanguageStore } from '../../../stores/languageStore';
import type { SurveyNavigationProps } from '../SurveyPage.types';

/**
 * SurveyNavigation Component
 *
 * Renders navigation buttons for moving through the survey (Previous, Next, Submit).
 * Adapts its layout based on whether the survey shows one question at a time or all
 * questions on a single page.
 *
 * Behavior:
 * - In one-at-a-time mode: Shows Previous and Next/Submit buttons
 * - In all-questions mode: Shows Previous and Next/Submit buttons
 * - In mobile one-page mode: Hides Previous, shows only Submit button
 * - Previous button is disabled on the first question (if shown)
 * - Submit button replaces Next button on the last question
 * - In mobile one-page mode, Submit is disabled until all questions are answered
 *
 * @param props - Component props
 * @returns The rendered navigation controls
 *
 * @example
 * <SurveyNavigation
 *   showOneAtATime={true}
 *   currentIndex={2}
 *   totalQuestions={10}
 *   onPrevious={handlePrevious}
 *   onNext={handleNext}
 *   onSubmit={handleSubmit}
 *   shouldHidePrevious={false}
 * />
 */
export const SurveyNavigation: React.FC<SurveyNavigationProps> = ({
  showOneAtATime,
  currentIndex,
  totalQuestions,
  onPrevious,
  onNext,
  onSubmit,
  shouldShowSubmit,
  shouldDisablePrevious,
  shouldDisableNext,
  shouldHidePrevious,
}) => {
  const languageStore = useLanguageStore();

  const isFirstQuestion = shouldDisablePrevious ?? (currentIndex === 0);
  const isLastQuestion = shouldShowSubmit ?? (currentIndex === totalQuestions - 1);
  const isNextDisabled = shouldDisableNext ?? false;

  if (showOneAtATime) {
    return (
      <div className="survey-navigation">
        {!shouldHidePrevious && (
          <button
            className="nav-button nav-button-secondary"
            onClick={onPrevious}
            disabled={isFirstQuestion}
          >
            {languageStore.previous}
          </button>
        )}
        {isLastQuestion ? (
          <button
            className="nav-button nav-button-primary"
            onClick={onSubmit}
            disabled={isNextDisabled}
          >
            {languageStore.submit}
          </button>
        ) : (
          <button
            className="nav-button nav-button-primary"
            onClick={onNext}
            disabled={isNextDisabled}
          >
            {languageStore.next}
          </button>
        )}
      </div>
    );
  }

  // All questions shown at once - show Next or Submit based on page position
  return (
    <div className="survey-navigation">
      {!shouldHidePrevious && (
        <button
          className="nav-button nav-button-secondary"
          onClick={onPrevious}
          disabled={isFirstQuestion}
        >
          {languageStore.previous}
        </button>
      )}
      {isLastQuestion ? (
        <button
          className="nav-button nav-button-primary"
          onClick={onSubmit}
          disabled={isNextDisabled}
        >
          {languageStore.submit}
        </button>
      ) : (
        <button
          className="nav-button nav-button-primary"
          onClick={onNext}
          disabled={isNextDisabled}
        >
          {languageStore.next}
        </button>
      )}
    </div>
  );
};
